/**
 * Infinite horizontal carousel: duplicates are seamless when scrolling right.
 * When scroll reaches the cloned set, position resets by one set width.
 */
class CompletaPedidoCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {number} */
  #setWidth = 0;
  /** @type {boolean} */
  #isJumping = false;

  connectedCallback() {
    this.#track = this.querySelector('[data-completa-pedido-track]');
    if (!this.#track) return;

    this.#measure();
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.querySelector('[data-completa-pedido-next]')?.addEventListener('click', this.#onNext);
    this.querySelector('[data-completa-pedido-prev]')?.addEventListener('click', this.#onPrev);
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.querySelector('[data-completa-pedido-next]')?.removeEventListener('click', this.#onNext);
    this.querySelector('[data-completa-pedido-prev]')?.removeEventListener('click', this.#onPrev);
  }

  #measure = () => {
    if (!this.#track) return;
    const sets = this.#track.querySelectorAll('[data-completa-pedido-set]');
    if (!sets.length) return;
    this.#setWidth = sets[0].offsetWidth;
  };

  #onResize = () => {
    this.#measure();
  };

  #onScroll = () => {
    if (!this.#track || this.#isJumping || !this.#setWidth) return;

    // Past the end of the first set → jump back one set (seamless loop to the right)
    if (this.#track.scrollLeft >= this.#setWidth - 1) {
      this.#isJumping = true;
      const nextLeft = this.#track.scrollLeft - this.#setWidth;
      this.#track.style.scrollBehavior = 'auto';
      this.#track.scrollLeft = nextLeft;
      // Force reflow so the jump isn't animated by CSS scroll-behavior
      void this.#track.offsetWidth;
      this.#track.style.scrollBehavior = '';
      requestAnimationFrame(() => {
        this.#isJumping = false;
      });
    }
  };

  #slideStep() {
    if (!this.#track) return 0;
    const slide = this.#track.querySelector('.completa-pedido__rec-slide');
    if (!slide) return 120;
    const styles = getComputedStyle(slide);
    const gap = parseFloat(styles.marginInlineEnd) || 0;
    return slide.offsetWidth + gap;
  }

  #onNext = (event) => {
    event.preventDefault();
    if (!this.#track) return;
    this.#measure();
    this.#track.scrollBy({ left: this.#slideStep(), behavior: 'smooth' });
  };

  #onPrev = (event) => {
    event.preventDefault();
    if (!this.#track) return;
    this.#measure();

    // If at the start, jump to the equivalent position in the second set, then scroll left
    if (this.#track.scrollLeft <= 2 && this.#setWidth > 0) {
      this.#isJumping = true;
      this.#track.scrollLeft += this.#setWidth;
      requestAnimationFrame(() => {
        this.#isJumping = false;
        this.#track?.scrollBy({ left: -this.#slideStep(), behavior: 'smooth' });
      });
      return;
    }

    this.#track.scrollBy({ left: -this.#slideStep(), behavior: 'smooth' });
  };
}

if (!customElements.get('completa-pedido-carousel')) {
  customElements.define('completa-pedido-carousel', CompletaPedidoCarousel);
}
